/**
 * Aurora Protocol Quiz — vanilla JS state machine, multi-step version.
 * Each question step is data-driven from the DOM. Only the 'goals' question
 * type drives product recommendations. All other answers become Klaviyo properties.
 *
 * Supports two display modes (set via data-display-mode on the section root):
 *   - "inline" : quiz renders in the page flow (original behavior).
 *   - "popup"  : quiz renders inside a hidden modal, opened by any element
 *                with a [data-quiz-open] attribute anywhere on the page.
 */

(function () {
  'use strict';

  class QuizFlow {
    constructor(rootEl) {
      this.root = rootEl;
      this.sectionId = rootEl.dataset.sectionId;
      this.isPopup = rootEl.dataset.displayMode === 'popup';
      this.isOpen = false;

      const dataScript = document.getElementById(`quiz-data-${this.sectionId}`);
      if (!dataScript) return;

      try {
        this.data = JSON.parse(dataScript.textContent);
      } catch (err) {
        console.error('Quiz data parse failed:', err);
        return;
      }

      this.questionSteps = Array.from(rootEl.querySelectorAll('[data-step="question"]'));
      this.totalQuestions = this.questionSteps.length;

      this.state = {
        currentIndex: 0,           // which question step (0-based)
        answers: {},               // keyed by question_key → array of option keys/labels
        recommendedProducts: []
      };

      this.cacheElements();
      this.bindQuestionEvents();
      this.bindResultEvents();
      this.renderCurrentStep();

      if (this.isPopup) this.setupModal();

      // Register instance so global triggers can find it.
      QuizFlow.instances.push(this);
      this.root._quizFlow = this;

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.resetTransientUI();
      });
    }

    /* ---------------------------------------------------------------------
     * Modal / popup behavior
     * ------------------------------------------------------------------- */

    setupModal() {
      // Close on the "X" button(s)
      this.root.querySelectorAll('[data-quiz-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      // Close on backdrop click (only when the overlay itself is the target)
      if (this.el.overlay) {
        this.el.overlay.addEventListener('click', (e) => {
          if (e.target === this.el.overlay) this.closeModal();
        });
      }

      // Bound handlers so we can add/remove them cleanly
      this._onKeydown = (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') {
          this.closeModal();
        } else if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      };
    }

    openModal() {
      if (!this.isPopup || this.isOpen) return;
      this.isOpen = true;
      this._lastFocus = document.activeElement;

      this.root.classList.add('is-open');
      document.body.classList.add('quiz-modal-open');
      document.addEventListener('keydown', this._onKeydown);

      // Focus the first sensible control inside the dialog.
      const focusTarget =
        (this.el.dialog &&
          this.el.dialog.querySelector(
            '[data-quiz-close], button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
          )) || this.el.dialog;
      if (focusTarget && focusTarget.focus) focusTarget.focus();

      this.scrollToTop();
    }

    closeModal() {
      if (!this.isPopup || !this.isOpen) return;
      this.isOpen = false;

      this.root.classList.remove('is-open');
      document.body.classList.remove('quiz-modal-open');
      document.removeEventListener('keydown', this._onKeydown);

      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    // Close the quiz modal after a cart add, letting the theme's cart drawer
    // animation take over first. No-op in inline mode.
    closeAfterCartAdd() {
      if (!this.isPopup || !this.isOpen) return;
      setTimeout(() => this.closeModal(), 250);
    }

    trapFocus(e) {
      if (!this.el.dialog) return;
      const focusables = this.el.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const visible = Array.from(focusables).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (visible.length === 0) return;

      const first = visible[0];
      const last = visible[visible.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // In popup mode we scroll the modal's scroll container, not the window.
    scrollToTop() {
      if (this.isPopup) {
        if (this.el.scrollContainer) {
          this.el.scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    /* ------------------------------------------------------------------- */

    resetTransientUI() {
      this.el.emailToast.hidden = true;
      this.el.emailToast.classList.remove('is-visible');

      const quickToast = document.querySelector('[data-quiz-toast]');
      if (quickToast) quickToast.style.opacity = '0';

      this.setLoading(this.el.pathDirectBtn, false);
      this.setLoading(this.el.pathEmailBtn, false);
    }

    cacheElements() {
      const $ = (sel) => this.root.querySelector(sel);
      this.el = {
        progressBar: $('[data-progress-bar]'),
        resultStep: $('[data-step="result"]'),
        resultIntro: $('[data-result-intro]'),
        productsContainer: $('[data-recommended-products]'),
        bundleStack: $('[data-bundle-stack]'),
        bundleName: $('[data-bundle-name]'),
        bundleDescription: $('[data-bundle-description]'),
        bundlePriceNow: $('[data-bundle-price-now]'),
        bundlePriceWas: $('[data-bundle-price-was]'),
        bundlePriceSave: $('[data-bundle-price-save]'),
        pathDirectBtn: $('[data-path-direct]'),
        pathEmailBtn: $('[data-path-email]'),
        emailInput: $('[data-email-input]'),
        emailError: $('[data-email-error]'),
        emailToast: $('[data-email-toast]'),
        resetBtn: $('[data-reset]'),
        // Modal-only elements (null in inline mode)
        overlay: $('[data-quiz-overlay]'),
        dialog: $('[data-quiz-dialog]'),
        scrollContainer: $('[data-quiz-scroll]')
      };
    }

    bindQuestionEvents() {
      this.questionSteps.forEach((step) => {
        const questionKey = step.dataset.questionKey;
        const type = step.dataset.questionType;
        const maxSelections = parseInt(step.dataset.maxSelections, 10) || 1;

        // Init answer store
        this.state.answers[questionKey] = [];

        // Option buttons
        step.querySelectorAll('[data-option-btn]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.toggleOption(step, btn, questionKey, type, maxSelections);
          });
        });

        // Next / continue
        const nextBtn = step.querySelector('[data-next]');
        if (nextBtn) {
          nextBtn.addEventListener('click', () => this.goNext());
        }

        // Back
        const backBtn = step.querySelector('[data-back]');
        if (backBtn) {
          backBtn.addEventListener('click', () => this.goBack());
        }
      });
    }

    bindResultEvents() {
      this.el.pathDirectBtn.addEventListener('click', () => this.handleDirectPath());
      this.el.pathEmailBtn.addEventListener('click', () => this.handleEmailPath());
      this.el.resetBtn.addEventListener('click', () => this.reset());

      this.el.emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleEmailPath();
        }
      });

      this.el.emailInput.addEventListener('input', () => {
        this.el.emailError.hidden = true;
        this.el.emailInput.classList.remove('has-error');
      });
    }

    toggleOption(step, btn, questionKey, type, maxSelections) {
      const optionKey = btn.dataset.optionKey;
      const currentAnswers = this.state.answers[questionKey];

      if (type === 'single_select') {
        // Deselect all siblings
        step.querySelectorAll('[data-option-btn]').forEach((otherBtn) => {
          otherBtn.classList.remove('is-selected');
          otherBtn.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        this.state.answers[questionKey] = [optionKey];
      } else {
        // Multi-select
        const idx = currentAnswers.indexOf(optionKey);
        if (idx > -1) {
          currentAnswers.splice(idx, 1);
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-pressed', 'false');
        } else {
          if (currentAnswers.length >= maxSelections) {
            this.shakeElement(btn);
            return;
          }
          currentAnswers.push(optionKey);
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
        }
      }

      this.updateStepUI(step, type, maxSelections);
    }

    updateStepUI(step, type, maxSelections) {
      const questionKey = step.dataset.questionKey;
      const count = this.state.answers[questionKey].length;
      const hint = step.querySelector('[data-selection-hint]');
      const nextBtn = step.querySelector('[data-next]');

      // Some steps may not have a next button — guard before touching it.
      if (nextBtn) {
        nextBtn.disabled = count === 0;

        // Change button label on final step
        const isLast = this.state.currentIndex === this.totalQuestions - 1;
        const nextLabel = nextBtn.querySelector('[data-next-label]');
        if (nextLabel) {
          nextLabel.textContent = isLast ? 'Build my protocol' : 'Continue';
        }
      }

      // Selection hint
      if (type === 'single_select') {
        if (hint) hint.textContent = count === 0 ? 'Choose one' : '';
      } else {
        if (hint) {
          if (count === 0) {
            hint.textContent = `Select up to ${maxSelections}`;
          } else if (count < maxSelections) {
            hint.textContent = `${count} selected · pick up to ${maxSelections - count} more`;
          } else {
            hint.textContent = `${count} selected — you're set`;
          }
        }
      }
    }

    renderCurrentStep() {
      this.questionSteps.forEach((step, i) => {
        step.hidden = i !== this.state.currentIndex;
      });
      this.el.resultStep.hidden = true;

      const currentStep = this.questionSteps[this.state.currentIndex];
      if (currentStep) {
        this.updateStepUI(
          currentStep,
          currentStep.dataset.questionType,
          parseInt(currentStep.dataset.maxSelections, 10) || 1
        );
      }

      this.updateProgress();
    }

    updateProgress() {
      const totalSteps = this.totalQuestions + 1; // +1 for result
      const currentPosition = this.state.currentIndex + 1;
      const pct = (currentPosition / totalSteps) * 100;
      this.el.progressBar.style.width = `${pct}%`;
    }

    goNext() {
      if (this.state.currentIndex < this.totalQuestions - 1) {
        this.state.currentIndex += 1;
        this.renderCurrentStep();
        this.scrollToTop();
      } else {
        this.submitQuiz();
      }
    }

    goBack() {
      if (this.state.currentIndex > 0) {
        this.state.currentIndex -= 1;
        this.renderCurrentStep();
        this.scrollToTop();
      }
    }

    computeRecommendations() {
      const goalsQuestionKey = this.findGoalsQuestionKey();
      if (!goalsQuestionKey) {
        console.warn('No goals question found');
        return;
      }

      const selectedGoals = this.state.answers[goalsQuestionKey] || [];
      const productMap = new Map();

      selectedGoals.forEach((goalKey) => {
        const goal = this.data.goals.find((g) => g.key === goalKey);
        if (!goal || !goal.products) return;

        goal.products.forEach((product) => {
          if (!product.available) return;
          const existing = productMap.get(product.handle);
          productMap.set(product.handle, {
            product,
            count: (existing ? existing.count : 0) + 1
          });
        });
      });

      this.state.recommendedProducts = Array.from(productMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, this.data.config.maxProducts)
        .map((entry) => entry.product);
    }

    findGoalsQuestionKey() {
      const goalsStep = this.questionSteps.find(
        (s) => s.dataset.questionType === 'goals'
      );
      return goalsStep ? goalsStep.dataset.questionKey : null;
    }

    submitQuiz() {
      this.computeRecommendations();

      if (this.state.recommendedProducts.length === 0) {
        console.warn('No available products matched selected goals');
        return;
      }

      this.renderResult();
      this.showResult();
      this.scrollToTop();
    }

    renderResult() {
      const goalsQuestionKey = this.findGoalsQuestionKey();
      const selectedGoals = this.state.answers[goalsQuestionKey] || [];
      const primaryGoal = this.data.goals.find((g) => g.key === selectedGoals[0]);

      this.el.resultIntro.textContent =
        (primaryGoal && primaryGoal.resultIntro) ||
        "Here's exactly what your cells need to thrive.";

      // Product cards
      this.el.productsContainer.innerHTML = '';
      this.state.recommendedProducts.forEach((product) => {
        this.el.productsContainer.appendChild(this.buildProductCard(product));
      });

      // Bundle stack
      this.el.bundleStack.innerHTML = '';
      this.state.recommendedProducts.forEach((product, i) => {
        if (i > 0) {
          const plus = document.createElement('span');
          plus.className = 'quiz-flow__bundle-plus';
          plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          this.el.bundleStack.appendChild(plus);
        }
        const thumb = document.createElement('div');
        thumb.className = 'quiz-flow__bundle-thumb';
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.loading = 'lazy';
        thumb.appendChild(img);
        this.el.bundleStack.appendChild(thumb);
      });

      // Bundle name
      const autoName = this.state.recommendedProducts
        .map((p) => p.title.replace(/^Liquid |^Liposomal /gi, ''))
        .join(' + ');
      this.el.bundleName.textContent =
        primaryGoal && primaryGoal.bundleName
          ? primaryGoal.bundleName
          : autoName;

      this.el.bundleDescription.textContent =
        (primaryGoal && primaryGoal.bundleDescription) || '';

      // Pricing
      const totalRaw = this.state.recommendedProducts.reduce(
        (sum, p) => sum + (p.priceRaw || 0),
        0
      );
      const bundleDiscount = this.data.config.bundleDiscountPercent / 100;
      const nowRaw = Math.round(totalRaw * (1 - bundleDiscount));
      const savedRaw = totalRaw - nowRaw;

      this.el.bundlePriceNow.textContent = this.formatMoney(nowRaw);
      this.el.bundlePriceWas.textContent = this.formatMoney(totalRaw);
      this.el.bundlePriceSave.textContent = `You save ${this.formatMoney(savedRaw)}`;
    }

    buildProductCard(product) {
      const card = document.createElement('a');
      card.className = 'quiz-flow__product';
      card.href = product.url;
      card.setAttribute('aria-label', `View ${product.title}`);

      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title;
      img.className = 'quiz-flow__product-image';
      img.loading = 'lazy';

      const body = document.createElement('div');
      body.className = 'quiz-flow__product-body';

      const title = document.createElement('h3');
      title.className = 'quiz-flow__product-title';
      title.textContent = product.title;

      body.appendChild(title);

      if (product.oneLiner) {
        const oneLiner = document.createElement('p');
        oneLiner.className = 'quiz-flow__product-oneliner';
        oneLiner.textContent = product.oneLiner;
        body.appendChild(oneLiner);
      }

      const price = document.createElement('div');
      price.className = 'quiz-flow__product-price';
      price.textContent = product.price;
      body.appendChild(price);

      card.appendChild(img);
      card.appendChild(body);
      return card;
    }

    showResult() {
      this.questionSteps.forEach((step) => { step.hidden = true; });
      this.el.resultStep.hidden = false;
      this.el.progressBar.style.width = '100%';
    }

    async handleDirectPath() {
      this.setLoading(this.el.pathDirectBtn, true);
      this.showQuickToast('Adding your bundle to cart…');
      try {
        await this.addBundleToCart();
        await this.applyDiscountSilently(this.data.config.bundleDiscountCode);
        this.showQuickToast('Added to cart!');
        // Cart drawer opens (theme) → close the quiz underneath it.
        this.closeAfterCartAdd();
      } catch (err) {
        console.error('Direct path failed:', err);
        this.showQuickToast('Something went wrong — please try again.');
      } finally {
        this.setLoading(this.el.pathDirectBtn, false);
      }
    }

    async handleEmailPath() {
      const email = this.el.emailInput.value.trim();

      if (!this.isValidEmail(email)) {
        this.el.emailError.hidden = false;
        this.el.emailInput.classList.add('has-error');
        this.el.emailInput.focus();
        return;
      }

      this.setLoading(this.el.pathEmailBtn, true);

      // Grant tracking consent before pushing to Klaviyo.
      await this.grantTrackingConsent();

      try {
        await this.pushToKlaviyo(email);
      } catch (err) {
        console.error('[Quiz] pushToKlaviyo threw:', err);
      }

      try {
        await this.addBundleToCart();
        await this.applyDiscountSilently(this.data.config.emailDiscountCode);
        // Only show success after the cart add actually succeeds.
        this.showToast();
        // Cart drawer opens (theme) → close the quiz underneath it.
        this.closeAfterCartAdd();
      } catch (err) {
        console.error('Email path failed:', err);
        this.showQuickToast('Something went wrong — please try again.');
      } finally {
        this.setLoading(this.el.pathEmailBtn, false);
      }
    }

    async grantTrackingConsent() {
      if (
        !window.Shopify ||
        !window.Shopify.customerPrivacy ||
        typeof window.Shopify.customerPrivacy.setTrackingConsent !== 'function'
      ) {
        return;
      }

      try {
        await new Promise((resolve) => {
          window.Shopify.customerPrivacy.setTrackingConsent(
            {
              analytics: true,
              marketing: true,
              preferences: true,
              sale_of_data: true
            },
            resolve
          );
        });
        console.log('[Quiz] Tracking consent granted');
      } catch (err) {
        console.warn('[Quiz] Consent grant failed:', err);
      }
    }

    async pushToKlaviyo(email) {
      const goalsQuestionKey = this.findGoalsQuestionKey();
      const selectedGoals = this.state.answers[goalsQuestionKey] || [];
      const primaryGoal = this.data.goals.find((g) => g.key === selectedGoals[0]);
      const productTitles = this.state.recommendedProducts.map((p) => p.title);
      const productHandles = this.state.recommendedProducts.map((p) => p.handle);
      const productUrls = this.state.recommendedProducts.map(
        (p) => `${window.location.origin}${p.url}`
      );

      // Build the profile properties
      const profileProps = {
        'Primary Goal': primaryGoal ? primaryGoal.label : selectedGoals[0],
        'All Selected Goals': selectedGoals.map((k) => {
          const g = this.data.goals.find((goal) => goal.key === k);
          return g ? g.label : k;
        }),
        'Recommended Products': productTitles,
        'Recommended Product Handles': productHandles,
        'Recommended Product URLs': productUrls,
        'Quiz Completed': true,
        'Signup Source': 'aurora_protocol_quiz',
        'Discount Code Issued': this.data.config.emailDiscountCode
      };

      this.questionSteps.forEach((step) => {
        const key = step.dataset.questionKey;
        if (key === goalsQuestionKey) return;
        const answers = this.state.answers[key];
        if (!answers || answers.length === 0) return;
        const labels = this.getLabelsForOptionKeys(step, answers);
        const propertyName = this.humanizeKey(key);
        profileProps[propertyName] = answers.length === 1 ? labels[0] : labels;
      });

      const trackProps = {
        ...profileProps,
        'Discount Code': this.data.config.emailDiscountCode,
        'Discount Percent': this.data.config.emailDiscountPercent
      };

      if (typeof window.klaviyo === 'undefined') {
        console.warn('[Quiz] Klaviyo not loaded');
        return;
      }

      console.log('[Quiz] Sending to Klaviyo:', { email, profileProps, trackProps });

      try {
        // Klaviyo's identify + track return Promises — await them
        await window.klaviyo.identify({ email: email, ...profileProps });
        await window.klaviyo.track(this.data.config.klaviyoEventName, trackProps);
        console.log('[Quiz] Klaviyo calls resolved successfully');
      } catch (err) {
        console.error('[Quiz] Klaviyo call failed:', err);
      }
    }

    getLabelsForOptionKeys(step, keys) {
      return keys.map((key) => {
        const btn = step.querySelector(`[data-option-key="${key}"]`);
        return btn ? btn.dataset.optionLabel : key;
      });
    }

    humanizeKey(key) {
      return key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }

    async addBundleToCart() {
      const items = this.state.recommendedProducts.map((p) => ({
        id: p.variantId,
        quantity: 1
      }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
      const result = await res.json();
      await this.refreshCartIcon();
      return result;
    }

    async refreshCartIcon() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();

        // Common Shopify theme cart-badge selectors — check your actual
        // cart icon markup in DevTools and adjust this list if none match.
        document.querySelectorAll('[data-cart-count], .cart-count-bubble, #cart-icon-bubble, .cart-count').forEach((el) => {
          el.textContent = cart.item_count;
          if ('hidden' in el) el.hidden = cart.item_count === 0;
        });

        // In case your theme listens for a cart-updated event somewhere.
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (err) {
        console.warn('[Quiz] Cart icon refresh failed:', err);
      }
    }

    showQuickToast(message, duration = 1600) {
      let toast = document.querySelector('[data-quiz-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.dataset.quizToast = '';
        toast.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#111;color:#fff;padding:10px 18px;border-radius:8px;font-size:14px;z-index:10000;opacity:0;transition:opacity .25s ease;pointer-events:none;';
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      requestAnimationFrame(() => { toast.style.opacity = '1'; });
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { toast.style.opacity = '0'; }, duration);
    }

    async applyDiscountSilently(code) {
      if (!code) {
        console.warn('[Quiz] applyDiscountSilently called with no code — a previously set discount cookie (if any) will remain active.');
        return;
      }
      try {
        const res = await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
        console.log('[Quiz] Discount applied:', code, res.status);
      } catch (err) {
        console.warn('[Quiz] Discount apply failed:', err);
      }
    }

    showToast() {
      this.el.emailToast.hidden = false;
      this.el.emailToast.classList.add('is-visible');
    }

    reset() {
      this.state.currentIndex = 0;
      this.state.recommendedProducts = [];
      Object.keys(this.state.answers).forEach((k) => {
        this.state.answers[k] = [];
      });

      this.root.querySelectorAll('[data-option-btn]').forEach((btn) => {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      });

      this.el.emailInput.value = '';
      this.el.emailError.hidden = true;
      this.el.emailToast.hidden = true;
      this.el.emailToast.classList.remove('is-visible');
      this.setLoading(this.el.pathDirectBtn, false);
      this.setLoading(this.el.pathEmailBtn, false);

      this.renderCurrentStep();
      this.scrollToTop();
    }

    setLoading(btn, isLoading) {
      if (isLoading) {
        btn.classList.add('is-loading');
        btn.disabled = true;
      } else {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }

    shakeElement(el) {
      el.classList.remove('shake');
      void el.offsetWidth;
      el.classList.add('shake');
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }
  }

  // Registry of live instances so triggers can find the right quiz.
  QuizFlow.instances = [];

  /**
   * Resolve which popup quiz a trigger should open.
   * A trigger may specify a target via data-quiz-open="<section-id>" (optional).
   * Falls back to the first popup-mode quiz on the page.
   */
  function findQuizForTrigger(targetId) {
    const popups = QuizFlow.instances.filter((q) => q.isPopup);
    if (targetId) {
      const match = popups.find(
        (q) =>
          q.sectionId === targetId ||
          q.root.id === targetId ||
          q.root.id === `quiz-flow-${targetId}`
      );
      if (match) return match;
    }
    return popups[0] || null;
  }

  // Global, delegated trigger handling — works for links added anywhere
  // (e.g. a Custom Liquid block under the product price).
  function bindGlobalTriggers() {
    if (document._quizTriggersBound) return;
    document._quizTriggersBound = true;

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quiz-open]');
      if (!trigger) return;

      e.preventDefault();
      const targetId = (trigger.getAttribute('data-quiz-open') || '').trim();
      const quiz = findQuizForTrigger(targetId);
      if (quiz) {
        quiz.openModal();
      } else {
        console.warn('[Quiz] No popup quiz found for trigger', trigger);
      }
    });
  }

  function init() {
    document.querySelectorAll('[id^="quiz-flow-"]').forEach((el) => {
      if (!el.dataset.quizInitialized) {
        new QuizFlow(el);
        el.dataset.quizInitialized = 'true';
      }
    });
    bindGlobalTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();